import { create } from 'zustand'

export interface HistoryItem {
  id: string
  taskId: string
  brand?: string
  model?: string
  modelUrl?: string
  thumbnailUrl?: string
  timestamp: string
  mode: 'image' | 'text' | 'multiimage'
  syncedToNft?: boolean
  nftId?: string
  name?: string
}

interface StoreState {
  // Auth state
  isAuthenticated: boolean
  authToken: string | null
  userEmail: string | null
  walletAddress: string | null // Keep for backward compatibility
  
  // Generation state
  currentModel: string | null
  generationStatus: string
  generationProgress: number
  error: string | null
  
  // History
  history: HistoryItem[]
  
  // Actions
  setAuth: (token: string | null, email: string | null) => void
  logout: () => void
  setCurrentModel: (url: string | null) => void
  setGenerationStatus: (status: string) => void
  setGenerationProgress: (progress: number) => void
  setError: (error: string | null) => void
  addHistory: (item: HistoryItem) => void
  removeHistory: (id: string) => void
  clearHistory: () => void
  updateHistoryItem: (id: string, updates: Partial<HistoryItem>) => void
}

export const useStore = create<StoreState>((set) => ({
  // Initial state
  isAuthenticated: !!localStorage.getItem('auth_token'),
  authToken: localStorage.getItem('auth_token'),
  userEmail: localStorage.getItem('user_email'),
  walletAddress: localStorage.getItem('wallet_address'),
  currentModel: null,
  generationStatus: '',
  generationProgress: 0,
  error: null,
  history: JSON.parse(localStorage.getItem('snap_history') || '[]'),
  
  // Actions
  setAuth: (token, email) => {
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
    if (email) {
      localStorage.setItem('user_email', email)
    } else {
      localStorage.removeItem('user_email')
    }
    set({ isAuthenticated: !!token, authToken: token, userEmail: email, walletAddress: email })
  },
  
  logout: () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_email')
    localStorage.removeItem('wallet_address')
    set({ isAuthenticated: false, authToken: null, userEmail: null, walletAddress: null })
  },
  
  setCurrentModel: (url) => set({ currentModel: url }),
  setGenerationStatus: (status) => set({ generationStatus: status }),
  setGenerationProgress: (progress) => set({ generationProgress: progress }),
  setError: (error) => set({ error }),
  
  addHistory: (item) => set((state) => {
    const newHistory = [item, ...state.history]
    localStorage.setItem('snap_history', JSON.stringify(newHistory))
    return { history: newHistory }
  }),
  
  removeHistory: (id) => set((state) => {
    const newHistory = state.history.filter((item) => item.id !== id)
    localStorage.setItem('snap_history', JSON.stringify(newHistory))
    return { history: newHistory }
  }),
  
  clearHistory: () => {
    localStorage.removeItem('snap_history')
    set({ history: [] })
  },
  
  updateHistoryItem: (id, updates) => set((state) => {
    const newHistory = state.history.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    )
    localStorage.setItem('snap_history', JSON.stringify(newHistory))
    return { history: newHistory }
  }),
}))
